/**
 * National labels of the unit types (naming audit step 5, 2026-10-24, Dr. Tatar).
 *
 * The KEY of a unit type is its function (unitTypeRegistry.js); the national name is a LABEL on that function. The same function is a
 * cavalry squadron in the US, a formation reconnaissance regiment in the UK, a "batalion de cercetare" in Romania, an Aufklärungsbataillon
 * in Germany. This table holds the national term of a battalion-level unit type for the nations of nationalStandards.js. DISPLAY ONLY: it
 * moves no number, the key stays the function in every table and in saved data.
 *
 * Only terms that were seen in a source are listed (status SOURCED: the name of a real unit or the structure of the nation's army;
 * DERIVED: built from a sourced term, e.g. "apărare AA" written out). A nation or a type without an entry shows the generic label
 * of the type (the NATO label), never a guess. NATO and MULTINATIONAL have no national terms.
 * The labels are in the language of the nation (the app language does not translate them).
 *
 * Sources (all 2026): Romanian unit names (Batalionul 341 Infanterie, 345 Artilerie, 348 Apărare AA, 168 Logistic, 912 Tancuri, 136 Geniu,
 * 266 Poliție Militară, 202 Apărare CBRN; 1st CIMIC Battalion) from the Romanian Land Forces structure page and Romanian news; US terms from the
 * MCoE Supplemental Manual 3-90 (2012) charts and Army news (83rd Chemical Battalion, 91st Civil Affairs Battalion, attack reconnaissance
 * battalions); German terms from bundeswehr.de and the Bundeswehr Lexikon (Panzergrenadierbataillon, Jägerbataillon, Aufklärungsbataillon,
 * Panzerpionierbataillon, Versorgungsbataillon); Polish terms from Polish army articles (batalion zmechanizowany) and the tool's own support
 * unit table (szwadron rozpoznawczy); French term from the tool's own table (escadron d'éclairage et d'investigation); UK terms from Janes via
 * unitTypes.js notes (formation reconnaissance regiment) and Army announcements (armoured infantry battalion). See
 * docs/research/RESEARCH_BRIGADE_SIZES.md, RESEARCH_LISTED_UNIT_TYPES.md.
 */

export const LABEL_STATUS = Object.freeze({ SOURCED: 'SOURCED', DERIVED: 'DERIVED' });

const L = (label, status, source) => Object.freeze({ label, status, source });

export const NATIONAL_UNIT_LABELS = Object.freeze({
  ROMANIAN: Object.freeze({
    INFANTRY:        L('Batalion de infanterie', 'SOURCED', 'Batalionul 341 Infanterie (9th Mechanized Brigade); Land Forces structure'),
    ARMOR:           L('Batalion de tancuri', 'SOURCED', 'Batalionul 912 Tancuri (9th Mechanized Brigade)'),
    ARTILLERY:       L('Batalion de artilerie', 'SOURCED', 'Batalionul 345 Artilerie (9th Mechanized Brigade)'),
    AIR_DEFENSE:     L('Batalion de apărare antiaeriană', 'DERIVED', 'Batalionul 348 Apărare AA (9th Mechanized Brigade), AA written out'),
    LOGISTICS:       L('Batalion logistic', 'SOURCED', 'Batalionul 168 Logistic (9th Mechanized Brigade)'),
    ENGINEERS:       L('Batalion de geniu', 'SOURCED', 'Batalionul 136 Geniu "Apulum" (10th Engineer Brigade)'),
    RECONNAISSANCE:  L('Batalion de cercetare', 'SOURCED', 'unitTypes.js v2.11 note (Romania has cercetare battalions); 102nd Reconnaissance Company'),
    CBRN_DEFENSE:    L('Batalion de apărare CBRN', 'SOURCED', 'Batalionul 202 Apărare CBRN "General Gheorghe Teleman"; 49th, 72nd CBRN battalions'),
    MILITARY_POLICE: L('Batalion de poliție militară', 'SOURCED', 'Batalionul 266 Poliție Militară "Radu Negru"; 265th'),
    CIVIL_AFFAIRS:   L('Batalion CIMIC', 'DERIVED', '1st CIMIC Battalion "General de corp de armată Ștefan Holban"')
  }),
  US: Object.freeze({
    MECHANIZED:      L('Combined Arms Battalion', 'SOURCED', 'SM 3-90 (2012), ABCT chart: CAB'),
    ARMOR:           L('Combined Arms Battalion', 'SOURCED', 'SM 3-90 (2012), ABCT chart: CAB'),
    RECONNAISSANCE:  L('Cavalry Squadron', 'SOURCED', 'SM 3-90 (2012): RECON SQDN; CRS IF10571: cavalry squadron'),
    ARTILLERY:       L('Field Artillery Battalion', 'SOURCED', 'SM 3-90 (2012): FA BN'),
    ENGINEERS:       L('Brigade Engineer Battalion', 'SOURCED', 'SM 3-90 (2012): BEB (in a brigade combat team)'),
    HQ:              L('Headquarters and Headquarters Company', 'SOURCED', 'SM 3-90 (2012): HHC'),
    CBRN_DEFENSE:    L('Chemical Battalion', 'SOURCED', 'Army news: 83rd Chemical Battalion, 20th CBRNE Command'),
    CIVIL_AFFAIRS:   L('Civil Affairs Battalion', 'SOURCED', '95th Civil Affairs Brigade: 91st, 92nd, 96th, 97th, 98th Civil Affairs Battalions'),
    ATTACK_AVIATION: L('Attack Reconnaissance Battalion', 'SOURCED', 'Army news: 1st Battalion (Attack Reconnaissance), 158th Aviation Regiment')
  }),
  GERMAN: Object.freeze({
    MECHANIZED:      L('Panzergrenadierbataillon', 'SOURCED', 'bundeswehr.de: Panzergrenadierbrigade 37'),
    ARMOR:           L('Panzerbataillon', 'DERIVED', 'Panzerbrigade 45 (tank and mechanized infantry battalions)'),
    INFANTRY:        L('Jägerbataillon', 'SOURCED', 'Bundeswehr Lexikon / suv.report: Panzergrenadierbrigade 41 has a Jägerbataillon'),
    RECONNAISSANCE:  L('Aufklärungsbataillon', 'SOURCED', 'Panzergrenadierbrigade 41 has an Aufklärungsbataillon'),
    ENGINEERS:       L('Panzerpionierbataillon', 'SOURCED', 'Panzergrenadierbrigade 41 has a Panzerpionierbataillon'),
    LOGISTICS:       L('Versorgungsbataillon', 'SOURCED', 'Panzergrenadierbrigade 41 has a Versorgungsbataillon')
  }),
  POLISH: Object.freeze({
    MECHANIZED:      L('Batalion zmechanizowany', 'SOURCED', 'pl.wikipedia Bataliony zmechanizowane; Senate expert report 2023'),
    RECONNAISSANCE:  L('Szwadron rozpoznawczy', 'SOURCED', 'nationalStandards.js SUPPORT_UNITS_BY_ECHELON / BRIGADE_RECON_KEY_BY_STANDARD; 17th Reconnaissance Company')
  }),
  FRENCH: Object.freeze({
    RECONNAISSANCE:  L("Escadron d'éclairage et d'investigation", 'SOURCED', 'nationalStandards.js BRIGADE_RECON_KEY_BY_STANDARD (escadron_eclairage_investigation)')
  }),
  BRITISH: Object.freeze({
    MECHANIZED:      L('Armoured Infantry Battalion', 'SOURCED', 'Future Soldier: heavy brigade combat teams, armoured infantry battalions'),
    ARMOR:           L('Armoured Regiment', 'DERIVED', 'British armour is organized in regiments'),
    RECONNAISSANCE:  L('Formation Reconnaissance Regiment', 'SOURCED', 'Janes via unitTypes.js note: Household Cavalry and Royal Lancers in the Deep Recce Strike brigade')
  })
});

/**
 * The national entry of a battalion-level unit type, or null (nation without terms, type without a sourced term).
 */
export function nationalLabelEntry(unitType, nationalStandard) {
  const nation = NATIONAL_UNIT_LABELS[nationalStandard];
  return (nation && nation[unitType]) || null;
}

/**
 * The label to show: the national term when one is listed, otherwise the generic label passed in (the NATO label of the type).
 * Display only.
 */
export function unitTypeLabel(unitType, nationalStandard, genericLabel) {
  const entry = nationalLabelEntry(unitType, nationalStandard);
  return entry ? entry.label : genericLabel;
}

// ---------------------------------------------------------------------------------------------------------------------------------------
// Brigade types (2026-10-24): the national term of a brigade type (the key stays the brigade type: MECHANIZED, ARMOR ...). The US has no
// "mechanized brigade" now: the heavy brigade is the Armored Brigade Combat Team, which is also what the card list of the tool follows.
// ---------------------------------------------------------------------------------------------------------------------------------------
export const NATIONAL_BRIGADE_LABELS = Object.freeze({
  US: Object.freeze({
    INFANTRY:    L('Infantry Brigade Combat Team', 'SOURCED', 'SM 3-90 (2012): IBCT'),
    MOUNTAIN:    L('Infantry Brigade Combat Team', 'DERIVED', 'SM 3-90 (2012): IBCT (the mountain brigade of the tool uses the IBCT card list)'),
    AIRBORNE:    L('Airborne Infantry Brigade Combat Team', 'DERIVED', 'IBCT with airborne battalions (2nd BCT 82nd Airborne, 173rd Airborne)'),
    MECHANIZED:  L('Armored Brigade Combat Team', 'SOURCED', 'SM 3-90 (2012): ABCT (the mechanized card list of the tool is the ABCT)'),
    ARMOR:       L('Armored Brigade Combat Team', 'SOURCED', 'SM 3-90 (2012): ABCT'),
    HEAVY:       L('Armored Brigade Combat Team', 'SOURCED', 'SM 3-90 (2012): ABCT'),
    STRYKER:     L('Stryker Brigade Combat Team', 'SOURCED', 'SM 3-90 (2012): SBCT'),
    AVIATION:    L('Combat Aviation Brigade', 'SOURCED', 'Army news: combat aviation brigade'),
    LOGISTICS:   L('Sustainment Brigade', 'SOURCED', 'Army sustainment brigade (the US name of the logistics brigade; the Army renamed logistics to sustainment in 2006)'),
    MEDICAL:     L('Medical Brigade', 'SOURCED', 'Wikipedia Medical brigade (United States): headquarters company about 65'),
    ENGINEERS:   L('Engineer Brigade', 'SOURCED', '130th Engineer Brigade')
  }),
  ROMANIAN: Object.freeze({
    MECHANIZED:  L('Brigadă mecanizată', 'SOURCED', 'Brigada 9 Mecanizată "Mărășești"; 81st, 15th Mechanized Brigade'),
    INFANTRY:    L('Brigadă de infanterie', 'SOURCED', 'Brigada 2 Infanterie "Rovine"'),
    ARMOR:       L('Brigadă blindată', 'SOURCED', '282nd Armored Brigade; Romanian press: brigadă blindată sau mecanizată'),
    MOUNTAIN:    L('Brigadă de vânători de munte', 'SOURCED', 'nationalStandards.js note (Vânători de Munte); 2nd and 61st Mountain Troops Brigades'),
    ENGINEERS:   L('Brigadă de geniu', 'SOURCED', 'Brigada 10 Geniu "Dunărea de Jos"')
  }),
  GERMAN: Object.freeze({
    MECHANIZED:  L('Panzergrenadierbrigade', 'SOURCED', 'bundeswehr.de: Panzergrenadierbrigade 37, 41'),
    ARMOR:       L('Panzerbrigade', 'SOURCED', 'Panzerbrigade 45 (Lithuania), Panzerlehrbrigade 9')
  }),
  BRITISH: Object.freeze({
    MECHANIZED:  L('Armoured Infantry Brigade', 'SOURCED', 'Janes: 1st Armoured Infantry Brigade'),
    ARMOR:       L('Heavy Brigade Combat Team', 'SOURCED', 'Future Soldier: two heavy brigade combat teams'),
    INFANTRY:    L('Light Brigade Combat Team', 'SOURCED', 'Future Soldier: two light brigade combat teams')
  }),
  POLISH: Object.freeze({
    MECHANIZED:  L('Brygada zmechanizowana', 'SOURCED', 'Senate expert report 2023; 17th Mechanized Brigade'),
    ARMOR:       L('Brygada pancerna', 'SOURCED', 'Wojsko Polskie brygady pancerne, 1st Warsaw Armoured Brigade')
  }),
  FRENCH: Object.freeze({
    MECHANIZED:  L('Brigade interarmes', 'SOURCED', 'Ministère des Armées: brigade interarmes (6 to 8 regiments)'),
    ARMOR:       L('Brigade interarmes', 'SOURCED', 'Ministère des Armées: brigade interarmes'),
    INFANTRY:    L('Brigade interarmes', 'SOURCED', 'Ministère des Armées: brigade interarmes')
  })
});

/** The brigade-type label to show: the national term when one is listed, else the generic label passed in. Display only. */
export function brigadeTypeLabel(brigadeType, nationalStandard, genericLabel) {
  const nation = NATIONAL_BRIGADE_LABELS[nationalStandard];
  return (nation && nation[brigadeType] && nation[brigadeType].label) || genericLabel;
}
